import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtNum, fmtEUR } from "../lib/api";
import { PageHeader, Surface, Btn, Field, Input, Select, StatusTag } from "../components/Primitives";
import { useAuth } from "../contexts/AuthContext";
import { Plus, X, ArrowRight, AirplaneTilt } from "@phosphor-icons/react";

const empty = {
  type_slug: "",
  manufacturer: "", model: "", make_model: "",
  registration: "", home_base: "",
  year_of_manufacture: "",
  hobbs: 0, tach_time: 0,
  fuel_type: "AVGAS_100LL", fuel_capacity: 0,
  engine_manufacturer: "", engine_model: "",
  engine_tbo_hours: "",
  max_members: 6,
  status: "available",
};

export default function Fleet() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isOwner = user?.role === "owner" || user?.role === "tenant_admin";
  const [list, setList] = useState([]);
  const [types, setTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [submitTypeOpen, setSubmitTypeOpen] = useState(false);
  const [newType, setNewType] = useState({
    manufacturer: "", model: "", seats: 4,
    fuel_type: "AVGAS_100LL", fuel_capacity_l: 0,
    engine_manufacturer: "", engine_model: "", engine_tbo_hours: 2000,
    default_max_members: 6, notes: "",
  });
  const [submittingType, setSubmittingType] = useState(false);

  const load = () => api.get("/aircraft").then((r) => setList(r.data));
  const loadTypes = () => api.get("/aircraft-types").then((r) => setTypes(r.data.types || [])).catch(() => {});
  useEffect(() => {
    load();
    loadTypes();
  }, []);

  const pickType = (slug) => {
    if (!slug) {
      setForm({ ...form, type_slug: "" });
      return;
    }
    const t = types.find((x) => x.slug === slug);
    if (!t) return;
    setForm({
      ...form,
      type_slug: slug,
      manufacturer: t.manufacturer,
      model: t.model,
      make_model: t.label,
      fuel_type: t.fuel_type,
      fuel_capacity: t.fuel_capacity_l,
      engine_manufacturer: t.engine_manufacturer,
      engine_model: t.engine_model,
      engine_tbo_hours: t.engine_tbo_hours,
      max_members: t.default_max_members,
    });
  };

  const save = async () => {
    setError(""); setBusy(true);
    try {
      // Strip empty-string ints so the backend Pydantic model is happy
      const payload = { ...form };
      if (payload.year_of_manufacture === "" || payload.year_of_manufacture == null) {
        delete payload.year_of_manufacture;
      } else {
        payload.year_of_manufacture = parseInt(payload.year_of_manufacture, 10);
      }
      if (payload.engine_tbo_hours === "" || payload.engine_tbo_hours == null) {
        delete payload.engine_tbo_hours;
      } else {
        payload.engine_tbo_hours = parseFloat(payload.engine_tbo_hours);
      }
      if (!payload.type_slug) delete payload.type_slug;
      const r = await api.post("/aircraft", payload);
      setOpen(false); setForm(empty);
      load();
      navigate(`/fleet/${r.data.id}`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to add aircraft");
    } finally {
      setBusy(false);
    }
  };

  const submitNewType = async () => {
    if (!newType.manufacturer || !newType.model) {
      setError("Manufacturer and model are required to submit a new type");
      return;
    }
    setSubmittingType(true);
    try {
      const r = await api.post("/aircraft-types", newType);
      // Refresh catalogue and auto-pick the new (pending) type
      await loadTypes();
      setForm({ ...form, type_slug: r.data.slug,
        manufacturer: r.data.manufacturer, model: r.data.model,
        make_model: `${r.data.manufacturer} ${r.data.model}`,
        fuel_type: r.data.fuel_type, fuel_capacity: r.data.fuel_capacity_l,
        engine_manufacturer: r.data.engine?.manufacturer,
        engine_model: r.data.engine?.model,
        engine_tbo_hours: r.data.engine?.tbo_hours,
        max_members: r.data.default_max_members,
      });
      setSubmitTypeOpen(false);
      setNewType({ manufacturer: "", model: "", seats: 4,
        fuel_type: "AVGAS_100LL", fuel_capacity_l: 0,
        engine_manufacturer: "", engine_model: "", engine_tbo_hours: 2000,
        default_max_members: 6, notes: "" });
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to submit new type");
    } finally {
      setSubmittingType(false);
    }
  };

  return (
    <div>
      <PageHeader overline="Operations" title="Fleet"
        subtitle="Each aircraft has its own bank account, reserves, cost rates and named pilots."
        actions={isOwner && <Btn data-testid="add-aircraft-btn" onClick={() => setOpen(true)}><Plus size={14} className="inline mr-1.5" />Add aircraft</Btn>}
      />
      <div className="p-8">
        {list.length === 0 ? (
          <Surface className="p-12 text-center">
            <div className="text-zinc-400 text-sm">No aircraft in the group yet.</div>
          </Surface>
        ) : (
          <div className="grid lg:grid-cols-2 gap-4" data-testid="aircraft-grid">
            {list.map((a) => (
              <button key={a.id} onClick={() => navigate(`/fleet/${a.id}`)}
                data-testid={`aircraft-card-${a.registration}`}
                className="text-left bg-[#141414] border border-zinc-800 hover:border-amber transition-colors p-6 group">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-1">{a.home_base || "Base —"}</div>
                    <div className="font-mono text-2xl text-amber tracking-tight">{a.registration}</div>
                    <div className="text-sm text-zinc-300 mt-1">{a.make_model}</div>
                  </div>
                  <StatusTag status={a.status} />
                </div>
                <div className="grid grid-cols-4 gap-3 text-xs border-t border-zinc-800 pt-4">
                  <div><div className="text-zinc-500 uppercase tracking-wider mb-1 font-mono text-[10px]">Hobbs</div><div className="font-mono text-zinc-100">{fmtNum(a.hobbs, 1)}</div></div>
                  <div><div className="text-zinc-500 uppercase tracking-wider mb-1 font-mono text-[10px]">Var €/hr</div><div className="font-mono text-zinc-100">{fmtNum(a.cost_settings?.rate_variable_eur_per_hour, 0)}</div></div>
                  <div><div className="text-zinc-500 uppercase tracking-wider mb-1 font-mono text-[10px]">Eng. reserve</div><div className="font-mono text-zinc-100">{fmtEUR(a.reserves?.engine_eur || 0)}</div></div>
                  <div className="text-right"><div className="text-zinc-500 uppercase tracking-wider mb-1 font-mono text-[10px]">Open</div><ArrowRight size={16} className="ml-auto text-zinc-500 group-hover:text-amber transition-colors" /></div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" data-testid="aircraft-modal">
          <Surface className="w-full max-w-2xl p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-medium flex items-center gap-2">
                <AirplaneTilt size={22} className="text-amber" weight="duotone" />
                Add aircraft
              </h3>
              <button onClick={() => setOpen(false)}><X size={20} className="text-zinc-500" /></button>
            </div>

            {/* Type picker — pre-fills the form */}
            <div className="mb-5 pb-5 border-b border-zinc-800">
              <Field label="Aircraft type (optional — pre-fills the form)" >
                <select
                  value={form.type_slug}
                  onChange={(e) => pickType(e.target.value)}
                  className="w-full h-11 bg-[#0F0F0F] border border-zinc-800 px-3 text-sm font-mono focus:outline-none focus:border-amber"
                  data-testid="aircraft-type-picker"
                >
                  <option value="">— pick a type to pre-fill —</option>
                  {types.map((t) => (
                    <option key={t.slug} value={t.slug}>
                      {t.label} · {t.fuel_type} · {t.engine_tbo_hours}h TBO
                      {t.is_pending ? "  [PENDING REVIEW]" : ""}
                    </option>
                  ))}
                </select>
              </Field>
              {form.type_slug && (
                <div className="mt-2 text-[11px] text-zinc-500 font-mono">
                  Type defaults applied. You can still edit any field below before saving. A standard maintenance program (annual, 100h, transponder/altimeter/pitot-static, ELT) will be seeded for this aircraft.
                </div>
              )}
              <div className="mt-2 text-[11px] text-zinc-500 flex justify-between items-center">
                <span>Don&apos;t see your aircraft type?</span>
                <button
                  type="button"
                  onClick={() => setSubmitTypeOpen(true)}
                  className="text-amber hover:underline text-[11px] font-mono uppercase tracking-wider"
                  data-testid="submit-new-type-btn"
                >
                  + Submit a new type
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Registration *">
                <Input data-testid="aircraft-registration" value={form.registration}
                       onChange={(e) => setForm({ ...form, registration: e.target.value })}
                       placeholder="YR-XYZ" />
              </Field>
              <Field label="Status">
                <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="available">Available</option>
                  <option value="in_maintenance">In maintenance</option>
                  <option value="grounded">Grounded</option>
                  <option value="stored">Stored</option>
                </Select>
              </Field>
              <Field label="Manufacturer">
                <Input data-testid="aircraft-manufacturer" value={form.manufacturer}
                       onChange={(e) => setForm({ ...form, manufacturer: e.target.value, make_model: `${e.target.value} ${form.model}`.trim() })} />
              </Field>
              <Field label="Model">
                <Input data-testid="aircraft-model" value={form.model}
                       onChange={(e) => setForm({ ...form, model: e.target.value, make_model: `${form.manufacturer} ${e.target.value}`.trim() })} />
              </Field>
              <Field label="Year of manufacture">
                <Input type="number" min="1900" max="2100" value={form.year_of_manufacture}
                       onChange={(e) => setForm({ ...form, year_of_manufacture: e.target.value })} />
              </Field>
              <Field label="Home base">
                <Input value={form.home_base} placeholder="LRCT"
                       onChange={(e) => setForm({ ...form, home_base: e.target.value })} />
              </Field>
              <Field label="Hobbs (h)">
                <Input type="number" step="0.1" value={form.hobbs}
                       onChange={(e) => setForm({ ...form, hobbs: parseFloat(e.target.value || 0) })} />
              </Field>
              <Field label="Tach time (h)">
                <Input type="number" step="0.1" value={form.tach_time}
                       onChange={(e) => setForm({ ...form, tach_time: parseFloat(e.target.value || 0) })} />
              </Field>
              <Field label="Fuel type">
                <Select value={form.fuel_type} onChange={(e) => setForm({ ...form, fuel_type: e.target.value })}>
                  <option value="AVGAS_100LL">AVGAS 100LL</option>
                  <option value="MOGAS_91UL">MOGAS 91UL</option>
                  <option value="MOGAS_95">MOGAS 95</option>
                  <option value="MOGAS_98">MOGAS 98</option>
                  <option value="JET_A1">Jet A-1</option>
                  <option value="JET_A">Jet A</option>
                  <option value="DIESEL">Diesel</option>
                </Select>
              </Field>
              <Field label="Fuel capacity (L)">
                <Input type="number" value={form.fuel_capacity}
                       onChange={(e) => setForm({ ...form, fuel_capacity: parseFloat(e.target.value || 0) })} />
              </Field>
              <Field label="Engine manufacturer">
                <Input value={form.engine_manufacturer}
                       onChange={(e) => setForm({ ...form, engine_manufacturer: e.target.value })} />
              </Field>
              <Field label="Engine model">
                <Input value={form.engine_model}
                       onChange={(e) => setForm({ ...form, engine_model: e.target.value })} />
              </Field>
              <Field label="Engine TBO (h)">
                <Input type="number" value={form.engine_tbo_hours}
                       onChange={(e) => setForm({ ...form, engine_tbo_hours: e.target.value })} />
              </Field>
              <Field label="Max members">
                <Input type="number" min="1" value={form.max_members}
                       onChange={(e) => setForm({ ...form, max_members: parseInt(e.target.value || 1, 10) })} />
              </Field>
            </div>

            {error && (
              <div className="mt-4 p-3 border border-red-900/60 bg-red-950/20 text-xs text-red-300 font-mono" data-testid="aircraft-error">
                {error}
              </div>
            )}

            <div className="text-xs text-zinc-500 mt-4">
              After creation you'll be taken to the aircraft detail page to configure reserves, cost rates, bank account and members.
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Btn variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Btn>
              <Btn onClick={save} disabled={busy || !form.registration} data-testid="aircraft-save-btn">
                {busy ? "Creating…" : "Create aircraft"}
              </Btn>
            </div>
          </Surface>
        </div>
      )}

      {/* Phase 6A — Submit new aircraft type modal */}
      {submitTypeOpen && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" data-testid="submit-type-modal">
          <Surface className="w-full max-w-xl p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-xl font-medium">Submit new aircraft type</h3>
              <button onClick={() => setSubmitTypeOpen(false)}><X size={20} className="text-zinc-500" /></button>
            </div>
            <div className="mb-5 p-3 border border-amber/40 bg-amber/5 text-[11px] text-amber/90 font-mono">
              Your submission will go to OwnerCAM&apos;s reviewers. While pending, only you and the team can see it — but you CAN start using it on a new aircraft right away. Once approved it becomes visible to every workspace.
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Manufacturer *">
                <Input value={newType.manufacturer} data-testid="new-type-manufacturer"
                       onChange={(e) => setNewType({ ...newType, manufacturer: e.target.value })} />
              </Field>
              <Field label="Model *">
                <Input value={newType.model} data-testid="new-type-model"
                       onChange={(e) => setNewType({ ...newType, model: e.target.value })} />
              </Field>
              <Field label="Seats">
                <Input type="number" min="1" value={newType.seats}
                       onChange={(e) => setNewType({ ...newType, seats: parseInt(e.target.value || 1, 10) })} />
              </Field>
              <Field label="Default max members">
                <Input type="number" min="1" value={newType.default_max_members}
                       onChange={(e) => setNewType({ ...newType, default_max_members: parseInt(e.target.value || 1, 10) })} />
              </Field>
              <Field label="Fuel type">
                <Select value={newType.fuel_type}
                        onChange={(e) => setNewType({ ...newType, fuel_type: e.target.value })}>
                  <option value="AVGAS_100LL">AVGAS 100LL</option>
                  <option value="MOGAS_91UL">MOGAS 91UL</option>
                  <option value="MOGAS_95">MOGAS 95</option>
                  <option value="MOGAS_98">MOGAS 98</option>
                  <option value="JET_A1">Jet A-1</option>
                  <option value="JET_A">Jet A</option>
                  <option value="DIESEL">Diesel</option>
                </Select>
              </Field>
              <Field label="Fuel capacity (L)">
                <Input type="number" value={newType.fuel_capacity_l}
                       onChange={(e) => setNewType({ ...newType, fuel_capacity_l: parseFloat(e.target.value || 0) })} />
              </Field>
              <Field label="Engine manufacturer">
                <Input value={newType.engine_manufacturer}
                       onChange={(e) => setNewType({ ...newType, engine_manufacturer: e.target.value })} />
              </Field>
              <Field label="Engine model">
                <Input value={newType.engine_model}
                       onChange={(e) => setNewType({ ...newType, engine_model: e.target.value })} />
              </Field>
              <Field label="Engine TBO (h)">
                <Input type="number" value={newType.engine_tbo_hours}
                       onChange={(e) => setNewType({ ...newType, engine_tbo_hours: parseFloat(e.target.value || 0) })} />
              </Field>
              <div />
              <div className="col-span-2">
                <Field label="Notes (optional)">
                  <textarea
                    value={newType.notes}
                    onChange={(e) => setNewType({ ...newType, notes: e.target.value })}
                    rows={3}
                    className="w-full bg-[#0F0F0F] border border-zinc-800 px-3 py-2 text-sm focus:outline-none focus:border-amber"
                    placeholder="Any context to help us review — variant, MTOW, special equipment, references…"
                  />
                </Field>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Btn variant="ghost" onClick={() => setSubmitTypeOpen(false)} disabled={submittingType}>Cancel</Btn>
              <Btn onClick={submitNewType} disabled={submittingType || !newType.manufacturer || !newType.model}
                   data-testid="submit-new-type-save-btn">
                {submittingType ? "Submitting…" : "Submit for review"}
              </Btn>
            </div>
          </Surface>
        </div>
      )}
    </div>
  );
}
